import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { storeResources } from '../resources';
import { IChatsInitialValues } from './types';
import {
  ThunkCreateChat,
  ThunkGetChat,
  ThunkGetChatMessages,
  ThunkGetChats,
  ThunkGetPusherConfig,
} from './thunks';
import { IChatMessage, ITechTyping } from '../../services/chats/types';

const initialState: IChatsInitialValues = {
  chats: [],
  messages: [],
  pusherConfig: {
    key: '',
    appId: '',
    cluster: '',
  },
  chat: {
    name: '',
    isClosed: false,
    ticketLink: '',
    ticketNumber: '',
    id: 0,
    contactName: '',
    ticketId: '',
    unreadCount: 0,
  },
  pendingMessages: [],
  activeChat: 0,
  notifications: {},
  techTyping: {
    ChatId: 0,
    IsTyping: false,
    IsTechTyping: false,
  },
};

const chatsSlice = createSlice({
  name: storeResources.CHATS,
  initialState,
  reducers: {
    addNewMessage: (state, action: PayloadAction<IChatMessage>) => {
      state.messages.push(action.payload);
    },
    setCurrentChatClosedStatus: (
      state,
      action: PayloadAction<{ chatId: number; status: boolean }>,
    ) => {
      state.chats = state.chats.map(chat =>
        +chat.id === +action.payload.chatId ? { ...chat, isClosed: action.payload.status } : chat,
      );
    },
    setActiveChat: (state, action: PayloadAction<number>) => {
      state.activeChat = action.payload;
    },
    updateMessageState: (
      state,
      action: PayloadAction<{ messageId: number; status: 'loading' | 'error' | undefined }>,
    ) => {
      const message = state.messages.find(m => m.id === action.payload.messageId);
      if (message) {
        message.status = action.payload.status;
      }
    },
    addLastMessageToChat: (
      state,
      action: PayloadAction<{ message: IChatMessage; chatId: number }>,
    ) => {
      state.chats = state.chats.map(chat =>
        +chat.id === +action.payload.chatId
          ? {
              ...chat,
              lastMessage: action.payload.message,
              lastMessageDate: new Date().toISOString(),
              unreadCount: chat.unreadCount + 1,
            }
          : chat,
      );

      if (state.activeChat === action.payload.chatId) {
        state.messages.push(action.payload.message);
      }
    },
    addTechTyping: (state, action: PayloadAction<ITechTyping>) => {
      state.techTyping = action.payload;
    },
    addNotification: (state, action: PayloadAction<number>) => {
      if (state.notifications) {
        state.notifications[action.payload] = (+state.notifications[action.payload] || 0) + 1;
      }
    },
    dropNotification: (state, action: PayloadAction<number>) => {
      if (state.notifications) {
        state.notifications[action.payload] = 0;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(ThunkGetChats.fulfilled, (state, action) => {
      state.chats = action.payload;
    });

    builder.addCase(ThunkGetChatMessages.fulfilled, (state, action) => {
      state.messages = action.payload;
    });

    builder.addCase(ThunkGetPusherConfig.fulfilled, (state, action) => {
      state.pusherConfig = action.payload;
    });

    builder.addCase(ThunkCreateChat.fulfilled, (state, action) => {
      state.chats = [action.payload, ...state.chats];
    });

    builder.addCase(ThunkGetChat.fulfilled, (state, action) => {
      state.chat = action.payload;
    });
  },
});

export default chatsSlice.reducer;
export const {
  addNewMessage,
  setCurrentChatClosedStatus,
  setActiveChat,
  updateMessageState,
  addLastMessageToChat,
  addTechTyping,
  addNotification,
  dropNotification,
} = chatsSlice.actions;
